import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, GraduationCap, Layers, PencilLine, FileText, Send, ArrowRight,
  AlertTriangle, CheckCircle2, Circle, ListChecks, BarChart3,
} from 'lucide-react';
import { useAuth, useIsAdmin, useHasPermission } from '../lib/auth';
import {
  getEvaluationPeriods, getDashboardStats, getDashboardOverview, getTeacherClasses,
  DashboardOverview, TeacherClass,
} from '../lib/db';
import { EvaluationPeriodConfig } from '../types';

type EstadoNotas = Awaited<ReturnType<typeof getDashboardStats>>;

/**
 * Panel de inicio.
 *
 * No repite el menú lateral: responde a "¿qué tengo que hacer ahora?".
 * Muestra, por este orden, lo que falta por configurar, las incoherencias
 * que conviene corregir, las cifras del año activo y el estado de las notas.
 * Cuando algo sale vacío explica el motivo en vez de ocultar el bloque.
 */
export function Dashboard() {
  const user = useAuth((state) => state.user);
  const isAdmin = useIsAdmin();
  const canViewGrades = useHasPermission('view_grades');

  const [resumen, setResumen] = useState<DashboardOverview | null>(null);
  const [periodos, setPeriodos] = useState<EvaluationPeriodConfig[]>([]);
  const [periodo, setPeriodo] = useState('');
  const [notas, setNotas] = useState<EstadoNotas | null>(null);
  const [cargandoNotas, setCargandoNotas] = useState(false);
  const [error, setError] = useState('');
  // Un profesor ve sus clases, no el centro entero.
  const esProfesor = !isAdmin && !!user?.teacherId;
  const [clases, setClases] = useState<TeacherClass[] | null>(null);

  useEffect(() => {
    Promise.all([
      getDashboardOverview(),
      getEvaluationPeriods(),
      esProfesor ? getTeacherClasses(user!.teacherId!) : Promise.resolve(null),
    ])
      .then(([r, p, c]) => {
        setResumen(r);
        setPeriodos(p);
        setClases(c);
        if (p.length) setPeriodo(p[0].key);
      })
      .catch(err => {
        console.error('Error cargando el inicio:', err);
        setError('No se ha podido cargar el resumen del centro.');
      });
  }, [esProfesor, user?.teacherId]);

  // Un curso sin año vale para todos los años.
  const clasesDelAno = (clases || []).filter(c => !c.academicYearId || c.academicYearId === resumen?.activeYearId);

  useEffect(() => {
    if (!resumen?.activeYear || !periodo) return;
    if (esProfesor && clasesDelAno.length === 0) return;
    setCargandoNotas(true);
    getDashboardStats(resumen.activeYear, periodo, esProfesor ? clasesDelAno.map(c => c.courseId) : undefined)
      .then(setNotas)
      .catch(err => console.error('Error cargando el estado de notas:', err))
      .finally(() => setCargandoNotas(false));
    // clasesDelAno se deriva de clases y resumen, que ya son dependencias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumen?.activeYear, periodo, clases, esProfesor]);

  const hora = new Date().getHours();
  const saludo = hora < 13 ? 'Buenos días' : hora < 20 ? 'Buenas tardes' : 'Buenas noches';

  // Pasos de puesta en marcha, en el orden en que hay que darlos.
  const pasos = resumen ? [
    { hecho: !!resumen.activeYear, titulo: 'Año académico activo', detalle: 'Define el curso escolar en el que vas a trabajar', href: '/settings?tab=academic-year' },
    { hecho: resumen.periodsCount > 0, titulo: 'Periodos de evaluación', detalle: 'Trimestres o evaluaciones en las que se ponen notas', href: '/settings?tab=evaluation-periods' },
    { hecho: resumen.coursesInActiveYear > 0, titulo: 'Cursos', detalle: 'Crea los cursos o grupos del centro', href: '/courses' },
    { hecho: resumen.teachers > 0, titulo: 'Profesores', detalle: 'Da de alta al claustro', href: '/teachers' },
    { hecho: resumen.students > 0, titulo: 'Alumnos', detalle: 'Matricula a los alumnos en sus cursos', href: '/students' },
    { hecho: resumen.teacherAssignments > 0, titulo: 'Reparto de asignaturas', detalle: 'Indica qué imparte cada profesor', href: '/teacher-subjects' },
  ] : [];
  const pendientes = pasos.filter(p => !p.hecho).length;

  const avisos = resumen ? [
    resumen.newerYear && {
      texto: `El año activo es ${resumen.activeYear}, pero existe ${resumen.newerYear}. Si ya has empezado el nuevo curso, márcalo como activo.`,
      href: '/settings?tab=academic-year', accion: 'Revisar años',
    },
    // Si el año activo está vacío porque hay uno más nuevo, el aviso anterior ya
    // explica la causa; repetirlo con otras palabras solo añade ruido.
    !resumen.newerYear && resumen.students > 0 && resumen.studentsInActiveYear === 0 && resumen.activeYear && {
      texto: `Ningún alumno está matriculado en un curso de ${resumen.activeYear}, así que no se pueden poner notas de este año.`,
      href: '/students', accion: 'Ver alumnos',
    },
    resumen.studentsWithoutCourse > 0 && {
      texto: `${resumen.studentsWithoutCourse} ${resumen.studentsWithoutCourse === 1 ? 'alumno no tiene' : 'alumnos no tienen'} curso asignado.`,
      href: '/students', accion: 'Asignar curso',
    },
    resumen.teachersWithoutSubjects > 0 && {
      texto: `${resumen.teachersWithoutSubjects} ${resumen.teachersWithoutSubjects === 1 ? 'profesor no tiene' : 'profesores no tienen'} asignaturas: no podrán introducir notas.`,
      href: '/teacher-subjects', accion: 'Asignar',
    },
    resumen.emailsFailed > 0 && {
      texto: `${resumen.emailsFailed} ${resumen.emailsFailed === 1 ? 'boletín no se pudo enviar' : 'boletines no se pudieron enviar'}.`,
      href: '/settings?tab=email-history', accion: 'Ver historial',
    },
  ].filter(Boolean) as { texto: string; href: string; accion: string }[] : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="px-8 pt-8 pb-6 bg-white border-b border-slate-100">
        <p className="text-sm font-medium text-indigo-600">{saludo}</p>
        <h1 className="mt-0.5 text-2xl font-bold text-slate-900">{user?.name || user?.username || 'Bienvenido'}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {resumen?.activeYear ? <>Año académico <span className="font-medium text-slate-700">{resumen.activeYear}</span></> : 'Sin año académico activo'}
        </p>
      </header>

      <div className="px-8 py-8 space-y-6 max-w-6xl">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!resumen && !error && <p className="text-sm text-slate-400">Cargando…</p>}

        {/* 1. Puesta en marcha */}
        {resumen && isAdmin && pendientes > 0 && (
          <section className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks className="w-4 h-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-slate-800">Primeros pasos</h2>
              <span className="ml-auto text-xs text-slate-500">{pasos.length - pendientes} de {pasos.length} completados</span>
            </div>
            <div className="mb-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${((pasos.length - pendientes) / pasos.length) * 100}%` }} />
            </div>
            <ol className="divide-y divide-slate-100">
              {pasos.map((p, i) => {
                const siguiente = !p.hecho && pasos.slice(0, i).every(x => x.hecho);
                return (
                  <li key={p.titulo}>
                    <Link to={p.href} className="flex items-center gap-3 py-2.5 group">
                      {p.hecho
                        ? <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
                        : <Circle className={`w-5 h-5 shrink-0 ${siguiente ? 'text-indigo-500' : 'text-slate-300'}`} />}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${p.hecho ? 'text-slate-400 line-through' : 'font-medium text-slate-800'}`}>{p.titulo}</p>
                        {!p.hecho && <p className="text-xs text-slate-500">{p.detalle}</p>}
                      </div>
                      {siguiente && <span className="text-xs font-medium text-indigo-600">Siguiente paso</span>}
                      {!p.hecho && <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />}
                    </Link>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* 2. Incoherencias */}
        {isAdmin && avisos.length > 0 && (
          <section className="space-y-2">
            {avisos.map(a => (
              <div key={a.texto} className="flex items-center gap-3 px-4 py-3 bg-amber-50 rounded-lg border border-amber-100">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="flex-1 text-sm text-amber-900">{a.texto}</p>
                <Link to={a.href} className="text-sm font-medium text-amber-700 hover:text-amber-900 whitespace-nowrap">{a.accion} →</Link>
              </div>
            ))}
          </section>
        )}

        {/* 3. Cifras */}
        {resumen && isAdmin && (
          <section className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Cifra icono={Users} etiqueta="Alumnos" valor={resumen.studentsInActiveYear}
              nota={resumen.students !== resumen.studentsInActiveYear ? `${resumen.students} en total` : 'en el año activo'} href="/students" />
            <Cifra icono={GraduationCap} etiqueta="Profesores" valor={resumen.teachers}
              nota={resumen.teachersWithoutSubjects ? `${resumen.teachersWithoutSubjects} sin asignaturas` : 'todos con asignaturas'} href="/teachers" />
            <Cifra icono={Layers} etiqueta="Cursos" valor={resumen.coursesInActiveYear} nota="vigentes" href="/courses" />
            <Cifra icono={Send} etiqueta="Boletines enviados" valor={resumen.emailsSent}
              nota={resumen.emailsFailed ? `${resumen.emailsFailed} con error` : 'por correo'} href="/settings?tab=email-history" />
          </section>
        )}

        {/* 4a. Profesor: sus clases */}
        {resumen && esProfesor && (
          <MisClases
            clases={clases || []}
            clasesDelAno={clasesDelAno}
            activeYear={resumen.activeYear}
            periodos={periodos}
            periodo={periodo}
            onPeriodo={setPeriodo}
            notas={notas}
            cargando={cargandoNotas}
          />
        )}

        {/* 4b. Dirección: estado de las notas del centro */}
        {resumen && !esProfesor && (canViewGrades || isAdmin) && (
          <section className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
              <h2 className="text-sm font-semibold text-slate-800">Estado de las notas</h2>
              {periodos.length > 1 && (
                <div className="flex gap-1 p-0.5 bg-slate-100 rounded-lg">
                  {periodos.map(ep => (
                    <button key={ep.key} onClick={() => setPeriodo(ep.key)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${periodo === ep.key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
                      {ep.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <EstadoDeNotas resumen={resumen} hayPeriodos={periodos.length > 0} notas={notas} cargando={cargandoNotas} />
          </section>
        )}

        {/* 5. Acciones frecuentes */}
        <section>
          <h2 className="mb-3 text-xs font-semibold tracking-widest text-slate-400 uppercase">Acciones frecuentes</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(canViewGrades || isAdmin) && <Accion icono={PencilLine} titulo="Introducir notas" href="/grades" />}
            {isAdmin && <Accion icono={FileText} titulo="Generar boletines" href="/report-cards" />}
            {isAdmin && <Accion icono={BarChart3} titulo="Ver estadísticas" href="/stats" />}
          </div>
        </section>
      </div>
    </div>
  );
}

function MisClases({ clases, clasesDelAno, activeYear, periodos, periodo, onPeriodo, notas, cargando }: {
  clases: TeacherClass[]; clasesDelAno: TeacherClass[]; activeYear: string | null;
  periodos: EvaluationPeriodConfig[]; periodo: string; onPeriodo: (p: string) => void;
  notas: EstadoNotas | null; cargando: boolean;
}) {
  // Los casos vacíos se explican pensando en el profesor: no puede arreglarlos
  // él, así que se le dice a quién acudir en vez de mandarle a una pantalla.
  let vacio: string | null = null;
  if (clases.length === 0) vacio = 'Todavía no tienes asignaturas asignadas. Dirección tiene que asignártelas en "Reparto de asignaturas".';
  else if (!activeYear) vacio = 'No hay un año académico activo. Avisa a dirección.';
  else if (clasesDelAno.length === 0) vacio = `Tus clases pertenecen a otro año académico y el activo es ${activeYear}. Avisa a dirección para que lo revise.`;

  const progreso = new Map((notas?.courseStats || []).map(c => [c.courseId, c]));

  return (
    <section className="p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
        <h2 className="text-sm font-semibold text-slate-800">Mis clases</h2>
        {!vacio && periodos.length > 1 && (
          <div className="flex gap-1 p-0.5 bg-slate-100 rounded-lg">
            {periodos.map(ep => (
              <button key={ep.key} onClick={() => onPeriodo(ep.key)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${periodo === ep.key ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}>
                {ep.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {vacio ? <p className="text-sm text-slate-500">{vacio}</p> : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {clasesDelAno.map(c => {
            const p = progreso.get(c.courseId);
            const total = p?.total ?? 0;
            const hechas = p?.withGrades ?? 0;
            const pct = total ? Math.round((hechas / total) * 100) : 0;
            return (
              <Link key={c.courseId} to={`/grades?course=${c.courseId}`}
                className="p-4 rounded-lg border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all group">
                <div className="flex justify-between items-start gap-2">
                  <p className="font-semibold text-slate-800 group-hover:text-indigo-600">{c.courseName}</p>
                  <ArrowRight className="w-4 h-4 mt-0.5 text-slate-300 group-hover:text-indigo-500 shrink-0" />
                </div>
                <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{c.subjects.join(' · ')}</p>
                <div className="mt-3">
                  {cargando ? <p className="text-xs text-slate-400">Cargando…</p>
                    : total === 0 ? <p className="text-xs text-slate-400">Sin alumnos matriculados</p>
                    : (<>
                      <div className="flex justify-between mb-1 text-xs">
                        <span className="text-slate-500">{hechas} de {total} con notas</span>
                        <span className={`font-semibold ${pct === 100 ? 'text-green-600' : 'text-slate-700'}`}>{pct === 100 ? 'Completo' : `${pct}%`}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </>)}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EstadoDeNotas({ resumen, hayPeriodos, notas, cargando }: {
  resumen: DashboardOverview; hayPeriodos: boolean; notas: EstadoNotas | null; cargando: boolean;
}) {
  // Cada caso vacío dice por qué está vacío y qué hacer.
  if (!resumen.activeYear) return <Vacio texto="Marca un año académico como activo para ver el progreso de las notas." href="/settings?tab=academic-year" accion="Configurar año" />;
  if (!hayPeriodos) return <Vacio texto="No hay periodos de evaluación configurados." href="/settings?tab=evaluation-periods" accion="Crear periodos" />;
  if (cargando || !notas) return <p className="text-sm text-slate-400">Cargando…</p>;
  if (notas.totalStudents === 0) return <Vacio texto={`No hay alumnos matriculados en cursos de ${resumen.activeYear}.`} href="/students" accion="Ver alumnos" />;

  const pct = Math.round((notas.studentsWithGrades / notas.totalStudents) * 100);
  const pendientes = notas.courseStats.filter(c => c.withGrades < c.total)
    .sort((a, b) => a.withGrades / a.total - b.withGrades / b.total);

  return (
    <>
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-slate-500">{notas.studentsWithGrades} de {notas.totalStudents} alumnos con notas</span>
          <span className="text-xs font-semibold text-slate-700">{pct}%</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {pendientes.length === 0 ? (
        <p className="flex items-center gap-1.5 text-sm text-green-600">
          <CheckCircle2 className="w-4 h-4" /> Todos los alumnos tienen notas en este periodo
        </p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {pendientes.map(c => (
            <Link key={c.courseId} to="/grades"
              className="flex justify-between items-center p-2.5 text-xs bg-amber-50 rounded-lg border border-amber-100 hover:border-amber-200 transition-colors">
              <span className="font-medium text-slate-700 truncate">{c.courseName}</span>
              <span className="ml-2 font-semibold text-amber-700 shrink-0">{c.withGrades}/{c.total}</span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function Vacio({ texto, href, accion }: { texto: string; href: string; accion: string }) {
  return (
    <div className="flex flex-wrap gap-3 items-center justify-between">
      <p className="text-sm text-slate-500">{texto}</p>
      <Link to={href} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">{accion} →</Link>
    </div>
  );
}

function Cifra({ icono: Icono, etiqueta, valor, nota, href }: {
  icono: typeof Users; etiqueta: string; valor: number; nota: string; href: string;
}) {
  return (
    <Link to={href} className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-indigo-200 hover:shadow transition-all group">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
        <Icono className="w-4 h-4 text-slate-400 group-hover:text-indigo-500" />{etiqueta}
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{valor}</p>
      <p className="text-xs text-slate-500 truncate">{nota}</p>
    </Link>
  );
}

function Accion({ icono: Icono, titulo, href }: { icono: typeof Users; titulo: string; href: string }) {
  return (
    <Link to={href} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:border-indigo-200 hover:shadow transition-all group">
      <div className="flex justify-center items-center w-9 h-9 bg-indigo-50 rounded-lg shrink-0">
        <Icono className="w-4 h-4 text-indigo-600" />
      </div>
      <span className="flex-1 text-sm font-medium text-slate-800 group-hover:text-indigo-600">{titulo}</span>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}
